import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColumnMode, NgxDatatableModule } from '@siemens/ngx-datatable';

import { NgSelectComponent } from '@ng-select/ng-select';
import { ToastrService } from '@shared/services/toastr.service';
import { AuthService } from '@shared/services/auth.service';
import { ROLES, ROLES_MAP, UserRole } from '@shared/services/admin-user.service';
import { fullName } from '@shared/utils/data-utils';
import { distinctUntilChanged, firstValueFrom } from 'rxjs';
import { isValidEmail } from '@shared/validators/validators';
import { OrganizationMember, OrganizationUserService } from '@shared/services/organization-user.service';
import { ConfirmService } from '@shared/services/confirm.service';
import { HelpComponent } from '@shared/components/help/help.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';
import { ModalBasicComponent } from '@shared/components/modal-basic/modal-basic.component';
import { LabelComponent } from '@shared/components/label/label.component';
import * as pager from '@shared/pager';
import { debounceTime } from 'rxjs/operators';
import { TranslateLabelPipe } from '@shared/pipe/filter.pipe';

@Component({
  selector: 'app-organization-users',
  templateUrl: './organization-users.component.html',
  styleUrls: ['./organization-users.component.scss'],
  imports: [
    HelpComponent,
    NgxDatatableModule,
    FormsModule,
    NgFor,
    NgIf,
    ModalBasicComponent,
    NgSelectComponent,
    LabelComponent,
    TranslatePipe,
    ReactiveFormsModule,
    TranslateLabelPipe,
  ],
})
export class OrganizationUsersComponent implements OnInit {
  ColumnMode = ColumnMode;
  @ViewChild('modalAddUser') modalAddUser: any;
  @ViewChild('addUserInput') addUserInput: NgSelectComponent;

  lastAddUserInput = '';
  users: OrganizationMember[];
  nbUsers: number = 0;
  page = 0;
  size = 10;
  total = 0;

  newUsers = [];
  newUsersRole: UserRole = 'VIEWER';
  filters = new FormControl('');

  constructor(
    public organizationUserService: OrganizationUserService,
    private toastr: ToastrService,
    public translate: TranslateService,
    public auth: AuthService,
    private confirmService: ConfirmService,
  ) {}

  ngOnInit() {
    this.loadUsers();
    this.subscribeToFormControl();
  }

  async addUser() {
    let promises: Promise<any>[] = [];
    for (let email of [...new Set(this.newUsers.map((e) => e.label.trim()))]) {
      if (!isValidEmail(email)) {
        this.toastr.error('InvalidEmail');
        return;
      }
      promises.push(firstValueFrom(this.organizationUserService.setUserRole(email, this.newUsersRole)));
    }

    await Promise.all(promises);
    this.loadUsers();
    this.toastr.success();
    this.modalAddUser.hide();
    this.newUsers = [];
    this.newUsersRole = 'VIEWER';
  }

  updateUser(email: string, role: UserRole) {
    this.organizationUserService.setUserRole(email, role).subscribe((_) => {
      this.loadUsers();
      this.toastr.success();
    });
  }

  async deleteUser(email) {
    this.organizationUserService.removeUser(email).subscribe((_) => {
      this.loadUsers();
      this.toastr.success();
    });
  }

  changePage(e) {
    this.page = e.page - 1;
    this.loadUsers();
  }

  onPasteEmails(event) {
    const value = event.clipboardData.getData('text/plain');

    if (value != null && value.match(/[,;]/)) {
      event.preventDefault();
      const split = value.split(/[,;]/);
      split.forEach((s) => (this.newUsers = [...this.newUsers, { label: s.trim() }]));
      event.target.value = '';
    }
  }

  onAddItem(value: { label: string }) {
    const input: string = value.label;

    if (input.includes(',')) {
      const newTags: Array<string> = input
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      this.newUsers = this.newUsers.filter((item: { label: string }): boolean => item !== value);

      for (let tag of newTags) {
        this.newUsers.push({ label: tag });
      }
    }
  }

  showModalUpdate(previous, email, event) {
    return this.confirmService.confirm(
      {
        html: this.translate.instant('ConfirmChangeRole', {
          email,
          role: this.translate.instant(ROLES_MAP[event.target.value]),
        }),
      },
      () => this.updateUser(email, event.target.value),
      () => (event.target.value = previous),
    );
  }

  showModalDelete(email) {
    this.confirmService.confirm(
      {
        html: this.translate.instant('DeleteUserAlert', { email }),
        title: this.translate.instant('supprimer'),
      },
      () => this.deleteUser(email),
    );
  }
  roleFilter = new FormControl<UserRole | null>(null);

  subscribeToFormControl() {
    this.filters.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => this.loadUsers());
    this.roleFilter.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.page = 0;
      this.loadUsers();
    });
  }

  loadUsers() {
    this.organizationUserService
      .getUsers({ filter: this.filters.value, role: this.roleFilter.value }, { number: this.page, size: this.size })
      .subscribe((res) => {
        this.users = res.content;
        this.nbUsers = res.totalElements;
      });
  }
  handleAddUserType(event) {
    this.lastAddUserInput = event.term;
  }

  handleAddUserBlur() {
    if (this.lastAddUserInput) {
      this.newUsers = [...this.newUsers, { label: this.lastAddUserInput }];
      this.lastAddUserInput = '';
    }
  }

  showModalAddUser() {
    this.modalAddUser.show();
    setTimeout(() => this.addUserInput.focus(), 1);
  }

  protected readonly fullName = fullName;
  protected readonly OWNER: UserRole = 'OWNER';
  protected readonly ROLES = ROLES;
  protected readonly ROLES_MAP = ROLES_MAP;
  protected readonly pager = pager;
}
